/**
 * Legendary weapon icon overrides — bespoke per-item icons for the 9 named
 * legendary weapons, replacing the generic kind-based icon they'd otherwise
 * share with every other sword/mace/staff/polearm of the same rarity.
 *
 * Design spec: docs/design/coil-art/LEGENDARY_WEAPON_SHEETS.md — read it
 * before touching this file; every palette/shape decision below traces to
 * a section there.
 *
 * Bespoke-ladder rung (ItemIconRegistry.ts `ALL_ICONS`), NOT the procedural
 * kind ladder — these are id-keyed overrides, checked before `inferIconLook`
 * ever runs. Each function returns raw SVG (this map's contract, matching
 * WeaponIcons.ts/PotionIcons.ts/etc.), built by decoding framedIcon's base64
 * output back to a string via svgFromBase64.
 */
import { framedIcon } from './rarityFrame';
import { diag } from './weaponIcons';
import { svgFromBase64 } from '@/game/rendering/svg/SVGUtils';
import type { ItemRarity } from '@/shared/CharacterTypes';

/** All 9 items in this file are legendary by construction (see the id list
 *  in ItemIconRegistry.ts). `ItemRarity`'s declared top tier is `'unique'`
 *  (see rarityFrame.ts naming note) but real content — and `framedIcon`'s
 *  halo gate — key off the string `'legendary'`, so this cast matches the
 *  same `as ItemRarity` pattern the generated content itself uses. */
const LEGENDARY = 'legendary' as ItemRarity;

// --- shared sword-silhouette hilt (used by #1 Stormbinder, #3 Dawnblade,
// #4 Gloomharrow — the 3 sword-kind legendaries). Authored VERTICALLY
// (blade tip up, guard/grip/pommel below), matching how every OTHER
// diag()-wrapped kind painter in weaponIcons.ts is authored (dagger/mace/
// axe/staff/polearm/bow all draw vertically, then diag() rotates once) —
// NOT the already-diagonal coordinates the procedural `sword` painter uses
// (that one skips diag() entirely because its own geometry is pre-rotated;
// mixing the two would double-rotate the blade). One `diag()` wrap here
// keeps every one of the 9 legendary icons on the same single-rotation
// contract the design sheet requires ("every weapon below gets diag()").
interface SwordSkin {
  bladeFill: string;
  bladeHighlight: string;
  glowFill: string;
  glowOpacity: number;
  guardFill: string;
  gripFill: string;
  wrapStroke: string;
  /** Extra distinguishing-detail markup (lightning/cracks/runes), drawn last. */
  extra: string;
}

function swordInner(skin: SwordSkin): string {
  return `
    <polygon points="12,1.5 14.3,15 12,17 9.7,15" fill="${skin.bladeFill}"/>
    <polygon points="12,1.5 12,15 9.7,15" fill="${skin.bladeHighlight}"/>
    <circle cx="12" cy="9" r="7" fill="${skin.glowFill}" opacity="${skin.glowOpacity}"/>
    <rect x="7" y="15.6" width="10" height="2.2" rx="1" fill="${skin.guardFill}" stroke="rgba(0,0,0,0.2)" stroke-width="0.8"/>
    <rect x="10.3" y="17.9" width="3.4" height="3.8" rx="1" fill="${skin.gripFill}" stroke="rgba(0,0,0,0.15)" stroke-width="0.5"/>
    <line x1="10.3" y1="19.3" x2="13.7" y2="19.3" stroke="${skin.wrapStroke}" stroke-width="0.8"/>
    <line x1="10.3" y1="20.7" x2="13.7" y2="20.7" stroke="${skin.wrapStroke}" stroke-width="0.8"/>
    <circle cx="12" cy="21.9" r="1.5" fill="${skin.guardFill}" stroke="rgba(0,0,0,0.2)" stroke-width="0.5"/>
    ${skin.extra}
  `;
}

// --- 1. Stormbinder, Blade of the Open Sky -----------------------
// Sheet §1: sword silhouette, electric-blue two-tone blade, unchanged
// gold-brown fittings, lightning-bolt strokes flanking the blade.
function stormbinder(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(swordInner({
    bladeFill: '#6ec3ff',
    bladeHighlight: '#eaf6ff',
    glowFill: '#4fd1ff',
    glowOpacity: 0.22,
    guardFill: '#8B6914',
    gripFill: '#4a3010',
    wrapStroke: '#6B4F12',
    extra: `
    <polyline points="7,4 9,7 6,9 8,12" fill="none" stroke="#eaf6ff" stroke-width="0.6"/>
    <polyline points="17,3 15,6 18,8 16,11" fill="none" stroke="#eaf6ff" stroke-width="0.6"/>`,
  })), LEGENDARY, size));
}

// --- 2. Cindermaul, the Burning Anvil ------------------------------------------
// Sheet §2: mace silhouette, molten-orange head, obsidian haft (material
// swap from generic mace's wood), ember teardrops drifting off the head.
function cindermaul(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(`
    <rect x="11.15" y="10" width="1.7" height="11" rx="0.85" fill="#1c1210"/>
    <rect x="10.8" y="18.8" width="2.4" height="2.4" rx="1" fill="#1c1210"/>
    <polygon points="12,0.8 13.5,3.4 16.5,3.4 15,6 16.5,8.6 13.5,8.6 12,11.2 10.5,8.6 7.5,8.6 9,6 7.5,3.4 10.5,3.4" fill="#ff6a1f"/>
    <circle cx="12" cy="6" r="2.7" fill="#8a2a0a"/>
    <circle cx="11" cy="5" r="0.9" fill="#ffd166"/>
    <circle cx="12" cy="6" r="6.5" fill="#ff8a3d" opacity="0.28"/>
    <ellipse cx="8.6" cy="2.2" rx="0.6" ry="0.9" fill="#ffd166" opacity="0.6"/>
    <ellipse cx="15.4" cy="1.6" rx="0.55" ry="0.85" fill="#ffd166" opacity="0.6"/>
    <ellipse cx="12" cy="0.4" rx="0.5" ry="0.8" fill="#ffd166" opacity="0.6"/>
  `), LEGENDARY, size));
}

// --- 3. Blighted Dawnblade -------------------------------------------------
// Sheet §3: sword silhouette, sickly fel-green blade, blackened fittings
// (do NOT reuse #1/#2's gold-brown), two thin jagged crack lines.
function blightedDawnblade(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(swordInner({
    bladeFill: '#5fbf3a',
    bladeHighlight: '#c9ffb0',
    glowFill: '#8aff4d',
    glowOpacity: 0.2,
    guardFill: '#1a1a1a',
    gripFill: '#0d0d0d',
    wrapStroke: '#1a1a1a',
    extra: `
    <line x1="9.5" y1="6" x2="13" y2="9" stroke="#0d1a08" stroke-width="0.5"/>
    <line x1="13" y1="10" x2="10.2" y2="13" stroke="#0d1a08" stroke-width="0.5"/>`,
  })), LEGENDARY, size));
}

// --- 4. Gloomharrow ----------------------------------------------------------
// Sheet §4: sword silhouette, near-black violet-steel blade (darkest in
// the set), dark iron fittings, 1-2 glowing rune dashes along the centerline.
function gloomharrow(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(swordInner({
    bladeFill: '#241b2e',
    bladeHighlight: '#4a3a5c',
    glowFill: '#7a3fd1',
    glowOpacity: 0.25,
    guardFill: '#2a2a2e',
    gripFill: '#1a1a1c',
    wrapStroke: '#2a2a2e',
    extra: `
    <line x1="12" y1="5" x2="12" y2="7" stroke="#b46bff" stroke-width="0.7"/>
    <line x1="12" y1="9.5" x2="12" y2="11.5" stroke="#b46bff" stroke-width="0.7"/>`,
  })), LEGENDARY, size));
}

// --- 5. Kingsmend, Hammer of the First Crowns -------------------------------------
// Sheet §5: mace silhouette, warm-gold head, ornate gold haft (material swap,
// NOT wood) with 2 dark trim bands, brightest glow of the set.
function kingsmend(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(`
    <rect x="11.15" y="10" width="1.7" height="11" rx="0.85" fill="#c9a227"/>
    <rect x="10.8" y="18.8" width="2.4" height="2.4" rx="1" fill="#8a6a10"/>
    <rect x="10.8" y="13.5" width="2.4" height="1" fill="#8a6a10"/>
    <rect x="10.8" y="17" width="2.4" height="1" fill="#8a6a10"/>
    <polygon points="12,0.8 13.5,3.4 16.5,3.4 15,6 16.5,8.6 13.5,8.6 12,11.2 10.5,8.6 7.5,8.6 9,6 7.5,3.4 10.5,3.4" fill="#ffe27a"/>
    <circle cx="12" cy="6" r="2.7" fill="#c9a227"/>
    <circle cx="11" cy="5" r="0.9" fill="#fffbe0"/>
    <circle cx="12" cy="6" r="7" fill="#ffe27a" opacity="0.3"/>
  `), LEGENDARY, size));
}

// --- 6. Greatstaff of the Last Warden ------------------------------------
// Sheet §6: staff shaft geometry, KEEP shaft, REPLACE orb-tip with a perched
// owl silhouette. Aged violet-grey wood (not the generic staff's brown).
function wardensGreatstaff(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(`
    <rect x="11.15" y="8.2" width="1.7" height="13.6" rx="0.85" fill="#5a4a6e"/>
    <rect x="11.15" y="8.2" width="0.6" height="13.6" rx="0.3" fill="#7a6a8e"/>
    <rect x="10.5" y="8.4" width="3" height="1.7" rx="0.8" fill="#3a2e48"/>
    <circle cx="12" cy="5.6" r="4.5" fill="#c9b6ff" opacity="0.22"/>
    <g>
      <ellipse cx="12" cy="5.6" rx="1.25" ry="1.75" fill="#1a1a1a"/>
      <path d="M11.1 4.6 Q10.6 5.6 11.1 6.6 L11.7 6.2 Q11.4 5.6 11.7 5 Z" fill="#3a3a3a"/>
      <polygon points="12.9,4.6 14,4.9 12.9,5.4" fill="#1a1a1a"/>
      <circle cx="12.55" cy="5.15" r="0.4" fill="#ffd76b"/>
    </g>
  `), LEGENDARY, size));
}

// --- 7. Hellbound Glaive — Main Hand -------------------------------------
// Sheet §7: polearm haft, blade head reshaped into a curved crescent/scythe
// (not the generic symmetric leaf). Fel-green, lit from within (light body,
// dark shadow edge — inverted value order from the steel/holy weapons).
// Dark chitinous haft, 2 flame-wisp accents licking the outer curve.
const WARGLAIVE_INNER = `
    <rect x="11.2" y="10.5" width="1.6" height="11.3" rx="0.8" fill="#2a1f14"/>
    <rect x="10.7" y="10.5" width="2.6" height="1.7" rx="0.8" fill="#1a130c"/>
    <path d="M12 1.4 Q18 2.6 17.2 8 Q16.2 11.6 12 11.2 Q13.6 6.2 12 1.4 Z" fill="#5fef6a"/>
    <path d="M12 1.4 Q9.4 3 9.8 6.8 Q10.2 9.6 12 11.2 Q10.6 6.2 12 1.4 Z" fill="#0d3a12"/>
    <circle cx="14" cy="6.5" r="6" fill="#5fef6a" opacity="0.22"/>
    <path d="M16 3.6 Q17.6 4.2 17 5.8 Q16.4 4.8 16 3.6 Z" fill="#c9ffb0" opacity="0.5"/>
    <path d="M15.8 8.4 Q17.4 9.2 16.6 10.6 Q16 9.4 15.8 8.4 Z" fill="#c9ffb0" opacity="0.5"/>
`;
function hellboundGlaiveMh(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(WARGLAIVE_INNER), LEGENDARY, size));
}

// --- 8. Hellbound Glaive — Off Hand --------------------------------------
// Sheet §8: IDENTICAL geometry to Main Hand, horizontally mirrored so the
// pair reads as a matched set. Mirror is applied INSIDE diag() (mirror the
// un-rotated inner art around its own 24x24 center) so the pair still shares
// the same 45-degree hero-diagonal head-to-top-right read after rotation.
function hellboundGlaiveOh(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(`
    <g transform="translate(24,0) scale(-1,1)">${WARGLAIVE_INNER}</g>
  `), LEGENDARY, size));
}

// --- 9. Skyshard, the Bow of Falling Light --------------------------------------------
// Sheet §9: bow limb+string shape (bespoke override corrects inferIconLook's
// 'sword' default — no bow keyword in the id/name). Pale silver-blue limb
// (not brown wood), string unchanged weight, physical arrow OMITTED and
// replaced with a starlight glow + tiny 4-point sparkle marks at the nock.
function skyshard(size: number = 32): string {
  return svgFromBase64(framedIcon(diag(`
    <path d="M4.5 13.2 Q12 3 19.5 13.2" fill="none" stroke="#d9e0f0" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M6 12 Q12 4.4 18 12" fill="none" stroke="#ffffff" stroke-width="0.6" stroke-linecap="round" opacity="0.8"/>
    <line x1="4.5" y1="13.2" x2="19.5" y2="13.2" stroke="#eaf3ff" stroke-width="0.8"/>
    <rect x="11.4" y="5.2" width="1.2" height="12.4" fill="#d9e0f0"/>
    <polygon points="12,2.4 10.2,6.2 13.8,6.2" fill="#d9e0f0"/>
    <polygon points="12,2.4 12,6.2 10.2,6.2" fill="#ffffff"/>
    <circle cx="12" cy="13.2" r="3" fill="#9fd4ff" opacity="0.3"/>
    <path d="M12 11.5 L12 12.5 M11.5 13.2 L12.5 13.2" stroke="#bfe3ff" stroke-width="0.5"/>
    <path d="M14.5 12.5 L15 13 M14.6 13.6 L15.1 13.1" stroke="#bfe3ff" stroke-width="0.5"/>
  `), LEGENDARY, size));
}

export const LEGENDARY_WEAPON_ICONS: Record<string, (size?: number) => string> = {
  wardens_greatstaff: wardensGreatstaff,
  hellbound_glaive_mh: hellboundGlaiveMh,
  hellbound_glaive_oh: hellboundGlaiveOh,
  skyshard,
  kingsmend: kingsmend,
  gloomharrow,
  stormbinder,
  cindermaul,
  blighted_dawnblade: blightedDawnblade,
};
